"use client"

import { createRoute } from "@tanstack/react-router"
import { Plus } from "lucide-react"

import { PageAction, PageHeader } from "@/components/graphite/page-header"
import { GraphiteSearches } from "@/components/searches/graphite-searches"
import { useSearches, type SearchRow } from "@/hooks/use-searches"
import MainAppRoute from "@/rootRoutes/MainApp"

/** The rows on screen, as a file. Escaped, so a brief with a comma survives. */
function exportCsv(rows: SearchRow[]) {
  const cell = (value: unknown) => {
    const text = value == null ? "" : String(value)
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
  }
  const lines = [
    ["search", "brief", "results", "criteria", "feeds", "owner", "saved"].join(","),
    ...rows.map((row) =>
      [
        row.title,
        row.brief ?? "",
        row.results ?? "",
        row.criteria.map((c) => `${c.label}: ${c.value}`).join(" | "),
        row.feeds ? `${row.feeds.title}${row.feeds.company ? ` @${row.feeds.company}` : ""}` : "",
        row.owner?.name ?? "",
        row.when ?? "",
      ]
        .map(cell)
        .join(","),
    ),
  ]

  const url = URL.createObjectURL(
    new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" }),
  )
  const link = document.createElement("a")
  link.href = url
  link.download = `searches_${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

/**
 * Saved searches, from artboard 7a.
 *
 * The page owns navigation and the export; the rows, the filters and the joins
 * come from `useSearches`.
 *
 * 7a draws the two header actions as a bordered pill and a filled one. They are
 * quiet words here, as everywhere else in the product — turn 9 predates that
 * language, and turn 14 draws the record's actions the way these read.
 */
function SearchesPage() {
  const searches = useSearches()
  const navigate = SearchesRoute.useNavigate()

  return (
    <div className="@container flex h-full min-h-0 flex-col">
      <PageHeader
        title="Searches"
        description="Saved searches and the longlists they feed."
        meta={`${searches.total} searches`}
        actions={
          <>
            <button
              type="button"
              onClick={() => exportCsv(searches.rows)}
              className="hidden whitespace-nowrap text-[12.5px] text-muted-foreground transition-colors hover:text-foreground @2xl:inline"
            >
              Export CSV
            </button>
            <PageAction tone="primary" onClick={() => navigate({ to: "/searches/new" })}>
              <Plus className="size-[15px]" />
              New Search
            </PageAction>
          </>
        }
      />

      <GraphiteSearches
        rows={searches.rows}
        jobOptions={searches.jobOptions}
        search={searches.search}
        onSearchChange={searches.setSearch}
        job={searches.job}
        onJobChange={searches.setJob}
        total={searches.total}
        onOpen={(row) =>
          navigate({
            to: "/searches/$searchId/results",
            params: { searchId: row.hash },
            search: { page: 1 },
          })
        }
        isLoading={searches.isLoading}
        isError={searches.isError}
      />
    </div>
  )
}

const SearchesRoute = createRoute({
  getParentRoute: () => MainAppRoute,
  path: "/searches",
  component: () => <SearchesPage />,
})

export default SearchesRoute
